import { useEffect, useRef } from 'react';
import { trpc } from '@/lib/trpc';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDateTime } from '@/lib/utils';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { usePagination } from '@/hooks/usePagination';

const ACTION_LABEL: Record<string, string> = {
  Repair: 'Reparación',
  Reassign: 'Reasignación',
  Retire: 'Retiro',
};

const ESTADOS = ['Solicitado', 'En proceso', 'Resuelto', 'Cancelado'] as const;

function estadoBadge(estado: string): string {
  if (estado === 'Resuelto') return 'bg-green-500';
  if (estado === 'En proceso') return 'bg-yellow-500';
  if (estado === 'Cancelado') return 'bg-gray-400';
  return 'bg-blue-500'; // Solicitado
}

export function QuipteamsSupportTab() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.inventory.listQuipteamsSupport.useQuery();
  const update = trpc.inventory.updateQuipteamsSupportStatus.useMutation({
    onSuccess: () => {
      utils.inventory.listQuipteamsSupport.invalidate();
      toast.success(t('inventory.quipteamssupporttab.estado_actualizado'));
    },
    onError: (e) => toast.error(e.message),
  });

  // Sync automático contra la API de QuipTeams al abrir la tab (+ botón manual)
  const sync = trpc.inventory.syncQuipteamsSupport.useMutation({
    onSuccess: (r) => {
      if (r.updated > 0) {
        toast.success(`${r.updated} pedido(s) actualizados desde QuipTeams`);
        utils.inventory.listQuipteamsSupport.invalidate();
      }
      if (r.error) toast.error(`Sync QuipTeams: ${r.error}`);
    },
    onError: (e) => toast.error(e.message),
  });
  const syncedOnce = useRef(false);
  useEffect(() => {
    if (!syncedOnce.current) {
      syncedOnce.current = true;
      sync.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows: any[] = data?.data ?? [];
  // Paginado: es el historial de pedidos de soporte y crece sin tope
  const { pageItems: pageRows, footer: paginationFooter } = usePagination(rows, 25);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
        <span>
          {t('inventory.quipteamssupporttab.el_estado_se_sincroniza')}{' '}
          <a
            href="https://dash.quipteams.com/assets/history"
            target="_blank"
            rel="noreferrer"
            className="text-primary inline-flex items-center gap-1 hover:underline"
          >
            {t('inventory.quipteamssupporttab.historial_de_quipteams')} <ExternalLink className="h-3 w-3" />
          </a>{' '}
          {t('inventory.quipteamssupporttab.o_corregir_el_estado_a_mano')}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="h-8 shrink-0 gap-1.5"
          disabled={sync.isPending}
          onClick={() => sync.mutate()}
        >
          <RefreshCw className={cn('h-3.5 w-3.5', sync.isPending && 'animate-spin')} />
          Sincronizar
        </Button>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Equipo</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Solicitante</TableHead>
                <TableHead>Notas</TableHead>
                <TableHead>Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    Cargando…
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    {t('inventory.quipteamssupporttab.todavia_no_hay_pedidos_de_soporte')}
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((r) => (
                  <TableRow key={r.supportId}>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {formatDateTime(r.requestedAt)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {r.deviceModel}
                      <span className="block text-xs text-muted-foreground font-normal">{r.serialNumber}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{ACTION_LABEL[r.actionType] ?? r.actionType}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{r.requesterEmail}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[280px]">{r.notes || '—'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge className={estadoBadge(r.estado)}>{r.estado}</Badge>
                        <select
                          value={r.estado}
                          disabled={update.isPending}
                          onChange={(e) =>
                            update.mutate({
                              supportId: r.supportId,
                              requestedAt: r.requestedAt,
                              estado: e.target.value as (typeof ESTADOS)[number],
                            })
                          }
                          className="text-xs border rounded px-1.5 py-1 bg-background"
                        >
                          {ESTADOS.map((e) => (
                            <option key={e} value={e}>
                              {e}
                            </option>
                          ))}
                        </select>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {paginationFooter && <div className="px-4 pb-3">{paginationFooter}</div>}
      </Card>
    </div>
  );
}
